// src/screens/TenderOrdersScreen.web.js - Fixed with proper cleanup
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
  Platform
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useFirebase } from '../context/FirebaseContext';
import OrderCardWeb from '../components/OrderCard.web';
import { ActivityIndicator, Button } from 'react-native-paper';

// Web-compatible icon component
const WebIcon = ({ name, size = 24, color = '#666' }) => {
  const iconMap = {
    'search': '🔍',
    'clear': '❌',
    'tune': '⚙️',
    'check': '✅',
    'gavel': '⚖️',
    'schedule': '⏰',
  };
  
  return (
    <Text style={{ fontSize: size, color, textAlign: 'center', width: size, height: size, lineHeight: size }}>
      {iconMap[name] || '◯'}
    </Text>
  );
};

const TenderOrdersScreenWeb = ({ navigation }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('הכל');
  const [selectedBranch, setSelectedBranch] = useState('הכל');
  const [dateFilter, setDateFilter] = useState('הכל');
  const [urgencyFilter, setUrgencyFilter] = useState('הכל');
  const [dateQuickFilter, setDateQuickFilter] = useState('הכל');
  
  const [franchiseeBranches, setFranchiseeBranches] = useState([]);
  const [linkedOrders, setLinkedOrders] = useState(new Set());
  
  const { user, isLoggingOut } = useAuth(); // Get isLoggingOut state
  const [entityName, setEntityName] = useState('');
  const { getTenderOrders, getFranchiseeBranches, linkOrderToBranch } = useFirebase();
  const [isInitialized, setIsInitialized] = useState(false);

  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize user data (with logout check)
  useEffect(() => {
    if (!user || !user.belongs || isLoggingOut) {
      console.log('🔄 TenderOrders: User logged out or logging out, resetting state');
      setIsInitialized(false);
      setEntityName('');
      setOrders([]);
      setFilteredOrders([]);
      setFranchiseeBranches([]);
      setLinkedOrders(new Set());
      return;
    }

    if (user.belongs === 'זכיין') {
      setEntityName(user.franchiseeName || 'זכיין');
      if (user.franchiseeId) {
        loadFranchiseeBranches();
      }
    } else if (user.belongs === 'מנהל סניף') {
      const branch = user.branches?.[0];
      setEntityName(branch?.branchName || 'סניף');
    }
    
    setIsInitialized(true);
  }, [user, isLoggingOut]);

  // Load orders when initialized (with logout check)
  useEffect(() => {
    if (isInitialized && user && !isLoggingOut) {
      loadOrders();
    } else if (isLoggingOut) {
      // Clear orders when logging out
      setOrders([]);
      setFilteredOrders([]);
      setLoading(false);
    }
  }, [user, isInitialized, isLoggingOut]);

  // Load franchisee branches
  const loadFranchiseeBranches = async () => {
    if (!user || user.belongs !== 'זכיין' || !user.franchiseeId || isLoggingOut) {
      return;
    }
    
    try {
      const branches = await getFranchiseeBranches(user.franchiseeId);
      
      // Check if component is still mounted and user still logged in
      if (!isMountedRef.current || isLoggingOut) {
        return;
      }
      
      if (Array.isArray(branches)) {
        const validBranches = branches.filter(branch => 
          branch && branch.id && (branch.name || branch.branchName)
        );
        setFranchiseeBranches(validBranches);
      }
    } catch (error) {
      console.error('Error loading franchisee branches:', error);
      if (isMountedRef.current && !isLoggingOut) {
        setFranchiseeBranches([]);
      }
    }
  };

  // Load orders
  const loadOrders = async () => {
    if (isLoggingOut) {
      console.log('🔄 TenderOrders: Logout in progress, skipping load');
      return;
    }

    if (!user || !user.branches || !Array.isArray(user.branches) || user.branches.length === 0) {
      // For demo purposes, create some dummy branches
      const dummyBranches = [{ id: 'demo', name: 'Demo Branch' }];
      try {
        setLoading(true);
        const tenderOrders = await getTenderOrders(dummyBranches);
        
        // Check if component is still mounted and user still logged in
        if (!isMountedRef.current || isLoggingOut) {
          return;
        }
        
        if (Array.isArray(tenderOrders)) {
          const filteredOrders = tenderOrders.filter(order => {
            const orderMode = order?.status;
            return orderMode !== 'הצעת מחיר' && orderMode !== 'סופק' && orderMode !== 'הועבר לסניף';
          });
          
          setOrders(filteredOrders || []);
          setFilteredOrders(filteredOrders || []);
          
          // Load linked orders status for franchisees
          if (user.belongs === 'זכיין') {
            const linked = new Set();
            filteredOrders.forEach(order => {
              if (order?.linkedToBranchId) {
                linked.add(order.id);
              }
            });
            setLinkedOrders(linked);
          }
        }
      } catch (error) {
        console.error('Error loading tender orders:', error);
        if (isMountedRef.current && !isLoggingOut) {
          setOrders([]);
          setFilteredOrders([]);
        }
      } finally {
        if (isMountedRef.current && !isLoggingOut) {
          setLoading(false);
        }
      }
      return;
    }

    try {
      setLoading(true);
      const tenderOrders = await getTenderOrders(user.branches);
      
      // Check if component is still mounted and user still logged in
      if (!isMountedRef.current || isLoggingOut) {
        return;
      }
      
      if (!Array.isArray(tenderOrders)) {
        setOrders([]);
        setFilteredOrders([]);
        setLoading(false);
        return;
      }
      
      const filteredOrders = tenderOrders.filter(order => {
        const orderMode = order?.status;
        return orderMode !== 'הצעת מחיר' && orderMode !== 'סופק' && orderMode !== 'הועבר לסניף';
      });
      
      setOrders(filteredOrders || []);
      setFilteredOrders(filteredOrders || []);
      
      // Load linked orders status for franchisees
      if (user.belongs === 'זכיין') {
        const linked = new Set();
        filteredOrders.forEach(order => {
          if (order?.linkedToBranchId) {
            linked.add(order.id);
          }
        });
        setLinkedOrders(linked);
      }
    } catch (error) {
      console.error('Error loading tender orders:', error);
      if (isMountedRef.current && !isLoggingOut) {
        setOrders([]);
        setFilteredOrders([]);
      }
    } finally {
      if (isMountedRef.current && !isLoggingOut) {
        setLoading(false);
      }
    }
  };

  // Refresh orders
  const onRefresh = async () => {
    if (isLoggingOut) return; // Don't refresh if logging out
    
    setRefreshing(true);
    await loadOrders();
    if (isMountedRef.current && !isLoggingOut) {
      setRefreshing(false);
    }
  };

  // Handle linking order to branch
  const handleLinkToBranch = async (orderId, branchId, branchDisplayName) => {
    if (isLoggingOut) return; // Don't link if logging out
    
    try {
      const result = await linkOrderToBranch(orderId, branchId, branchDisplayName);
      
      // Check if component is still mounted and user still logged in
      if (!isMountedRef.current || isLoggingOut) {
        return;
      }
      
      if (result.success) {
        // Remove the order from tender orders since it's now linked
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        setFilteredOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        setLinkedOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
        
        // Refresh data
        setTimeout(() => {
          if (isMountedRef.current && !isLoggingOut) {
            loadOrders();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error linking order to branch:', error);
      if (Platform.OS === 'web' && !isLoggingOut) {
        alert(`שגיאה בקישור הזמנה לסניף: ${error.message}`);
      }
    }
  };

  // Filter logic (with logout check)
  useEffect(() => {
    if (!isLoggingOut) {
      applyFilters();
    }
  }, [orders, searchQuery, selectedStatus, selectedBranch, dateFilter, urgencyFilter, dateQuickFilter, isLoggingOut]);

  const applyFilters = () => {
    if (isLoggingOut) return; // Don't apply filters if logging out

    let filtered = [...orders];

    // Apply search
    if (searchQuery.trim()) {
      filtered = filtered.filter(order => 
        order.tenderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.organizationName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedStatus !== 'הכל') {
      filtered = filtered.filter(order => order.status === selectedStatus);
    }

    // Apply date filters (simplified for web)
    if (dateQuickFilter !== 'הכל') {
      const now = new Date();
      filtered = filtered.filter(order => {
        const submissionDate = new Date(order.customerOrderDate);
        
        switch (dateQuickFilter) {
          case 'היום':
            return submissionDate.toDateString() === now.toDateString();
          case 'מחר':
            const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
            return submissionDate.toDateString() === tomorrow.toDateString();
          default:
            return true;
        }
      });
    }

    setFilteredOrders(filtered);
  };

  // Helper functions
  const getUniqueStatuses = () => {
    const statuses = [...new Set(orders.map(order => order.status).filter(Boolean))];
    return ['הכל', ...statuses.filter(status => status !== 'סופק')];
  };

  const clearAllFilters = () => {
    if (isLoggingOut) return; // Don't clear filters if logging out
    
    setSearchQuery('');
    setSelectedStatus('הכל');
    setSelectedBranch('הכל');
    setDateFilter('הכל');
    setUrgencyFilter('הכל');
    setDateQuickFilter('הכל');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (selectedStatus !== 'הכל') count++;
    if (selectedBranch !== 'הכל') count++;
    if (dateFilter !== 'הכל') count++;
    if (urgencyFilter !== 'הכל') count++;
    if (dateQuickFilter !== 'הכל') count++;
    return count;
  };

  const handleOrderPress = (order) => {
    if (isLoggingOut) return; // Don't navigate if logging out
    
    if (navigation && navigation.navigate) {
      navigation.navigate('OrderDetails', { order, orderType: 'tender' });
    }
  };

  const QuickFilterChip = ({ title, isActive, onPress, color = '#007AFF', disabled = false }) => (
    <TouchableOpacity
      style={[
        styles.filterChip, 
        isActive && [styles.activeFilterChip, { backgroundColor: color, borderColor: color }],
        disabled && styles.disabledChip
      ]}
      onPress={onPress}
      disabled={disabled}
    >
      <Text style={[styles.filterChipText, isActive && styles.activeFilterChipText]}>
        {title}
      </Text>
    </TouchableOpacity>
  );

  // Loading states
  if (!user || !user.belongs || isLoggingOut) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>
          {isLoggingOut ? 'מתנתק...' : 'טוען נתונים...'}
        </Text>
      </View>
    );
  }

  if (!isInitialized) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>מכין את המערכת...</Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>טוען הזמנות מכרז...</Text>
      </View>
    );
  }

  const statusFilters = getUniqueStatuses();
  const dateQuickFilters = ['הכל', 'היום', 'מחר'];

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <WebIcon name="search" size={20} color="#666" />
          <TextInput
            style={styles.searchInput}
            placeholder="חיפוש לפי מספר מכרז, ארגון, תיאור או מיקום..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
            editable={!isLoggingOut} // Disable input during logout
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} disabled={isLoggingOut}>
              <WebIcon name="clear" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>
        
        <TouchableOpacity 
          style={[styles.filterButton, getActiveFiltersCount() > 0 && styles.activeFilterButton]}
          onPress={() => setShowAdvancedFilters(true)}
          disabled={isLoggingOut}
        >
          <WebIcon name="tune" size={20} color={getActiveFiltersCount() > 0 ? "#fff" : "#007AFF"} />
          {getActiveFiltersCount() > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{getActiveFiltersCount()}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Quick Filters */}
      <View style={styles.filtersAndResultsContainer}>
        <View style={styles.filterRowContainer}>
          <Text style={styles.filterRowLabel}>סטטוס:</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.filterRowScroll}
            contentContainerStyle={styles.filterRowContent}
          >
            {statusFilters.map((status) => (
              <QuickFilterChip
                key={status}
                title={status}
                isActive={selectedStatus === status}
                onPress={() => !isLoggingOut && setSelectedStatus(status)}
                disabled={isLoggingOut}
              />
            ))}
          </ScrollView>
        </View>

        <View style={styles.filterRowContainer}>
          <Text style={styles.filterRowLabel}>תאריך:</Text>
          <View style={styles.dateFiltersContainer}>
            {dateQuickFilters.slice(1).map((dateFilterOption) => (
              <QuickFilterChip
                key={dateFilterOption}
                title={dateFilterOption}
                isActive={dateQuickFilter === dateFilterOption}
                onPress={() => !isLoggingOut && setDateQuickFilter(dateFilterOption)}
                color="#4CAF50"
                disabled={isLoggingOut}
              />
            ))}
          </View>
        </View>

        <View style={styles.resultsContainer}>
          <Text style={styles.resultsText}>
            {filteredOrders.length} מתוך {orders.length} מכרזים
          </Text>
          {getActiveFiltersCount() > 0 && !isLoggingOut && (
            <TouchableOpacity onPress={clearAllFilters}>
              <Text style={styles.clearFiltersText}>נקה מסננים</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Orders List */}
      <FlatList
        data={filteredOrders}
        keyExtractor={(item, index) => item?.id || `order-${index}`}
        renderItem={({ item }) => {
          if (!item || !item.id) return null;
          return (
            <OrderCardWeb 
              order={item} 
              onPress={() => handleOrderPress(item)}
              userType={user?.belongs || ''}
              franchiseeBranches={franchiseeBranches}
              onLinkToBranch={handleLinkToBranch}
              isLinked={linkedOrders.has(item.id)}
            />
          );
        }}
        contentContainerStyle={styles.listContainer}
        style={styles.flatListStyle}
        showsVerticalScrollIndicator={false}
        scrollEnabled={!isLoggingOut} // Disable scrolling during logout
        refreshControl={
          <RefreshControl 
            refreshing={refreshing} 
            onRefresh={onRefresh}
            colors={['#007AFF']}
            tintColor="#007AFF"
            enabled={!isLoggingOut}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <WebIcon name="gavel" size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {getActiveFiltersCount() > 0 ? 'לא נמצאו מכרזים התואמים למסננים' : 'אין מכרזים זמינים'}
            </Text>
            {getActiveFiltersCount() > 0 && !isLoggingOut && (
              <TouchableOpacity style={styles.clearFiltersButton} onPress={clearAllFilters}>
                <Text style={styles.clearFiltersButtonText}>נקה מסננים</Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  searchContainer: {
    flexDirection: 'row',
    padding: 12,
    paddingBottom: 4,
    backgroundColor: '#fff',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    paddingHorizontal: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 8,
    textAlign: 'right',
    marginHorizontal: 8,
  },
  filterButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  activeFilterButton: {
    backgroundColor: '#007AFF',
  },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF3B30',
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  filtersAndResultsContainer: {
    backgroundColor: '#fff',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  filterRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#fff',
  },
  filterRowLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    marginRight: 12,
    width: 60,
  },
  filterRowScroll: {
    flex: 1,
  },
  filterRowContent: {
    paddingRight: 16,
  },
  dateFiltersContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeFilterChip: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  disabledChip: {
    opacity: 0.6,
  },
  filterChipText: {
    fontSize: 13,
    color: '#666',
    fontWeight: '500',
  },
  activeFilterChipText: {
    color: '#fff',
  },
  resultsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  resultsText: {
    fontSize: 14,
    color: '#666',
  },
  clearFiltersText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  flatListStyle: {
    flex: 1,
  },
  listContainer: {
    paddingHorizontal: 12,
    paddingTop: 4,
    paddingBottom: 20,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyText: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  clearFiltersButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearFiltersButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
});

export default TenderOrdersScreenWeb;